import { Store } from '@betgames/bg-state-manager';
import { BettingOptionsFormat } from '@betgames/bg-tools';
import { GameId } from '@bg-shared/enums/GameId';
import { DealerLanguage } from '@bg-shared//enums/DealerLanguage';
import { DEFAULT_ROULETTE_NEIGHBOR_COUNT } from '@bg-shared/constants/constants';
import { isNil } from 'lodash-unified';
import { IPlayerSettingsModel } from './interfaces';

export default class PlayerSettingsStore extends Store<IPlayerSettingsModel> {
    constructor() {
        super({
            key: 'PlayerSettings',
            default: {
                casino: {
                    enabled: false,
                    welcomeModalSeen: false,
                    fallbackTooltipSeen: false,
                },
                gui: {
                    enabled: false,
                    tipSeen: false,
                    forcedOnLowQuality: false,
                },
                onlyRngAnimations: true,
                oddsFormat: null,
                selectedVODByGame: null,
                autoShowRouletteStatistics: true,
                rouletteNeighbourCount: DEFAULT_ROULETTE_NEIGHBOR_COUNT,
                autoPlayTooltipSeen: false,
            },
            persist: true,
            migration: {
                version: 3,
                callback(cache: IPlayerSettingsModel): IPlayerSettingsModel {
                    // @ts-expect-error "rngRouletteNeighbourCount" was removed
                    delete cache.rngRouletteNeighbourCount;
                    // @ts-expect-error "shova" was removed
                    delete cache.shova;
                    // @ts-expect-error "gamesHowToPlay" was removed
                    delete cache.gamesHowToPlay;
                    // @ts-expect-error "dealerLanguage" was removed
                    delete cache.dealerLanguage;
                    // @ts-expect-error "selectedDealerByGame" was removed
                    delete cache.selectedDealerByGame;

                    // @ts-expect-error "rouletteAutoPlayTooltipSeen" was changed to "autoPlayTooltipSeen"
                    if (!isNil(cache.rouletteAutoPlayTooltipSeen)) {
                        // @ts-expect-error "rouletteAutoPlayTooltipSeen" was changed to "autoPlayTooltipSeen"
                        cache.autoPlayTooltipSeen = cache.rouletteAutoPlayTooltipSeen;
                        // @ts-expect-error "rouletteAutoPlayTooltipSeen" was changed to "autoPlayTooltipSeen"
                        delete cache.rouletteAutoPlayTooltipSeen;
                    }

                    if (isNil(cache.oddsFormat)) {
                        delete cache.oddsFormat;
                    }

                    if (isNil(cache.selectedVODByGame)) {
                        delete cache.selectedVODByGame;
                    }

                    return cache;
                },
            },
        });
    }

    public set rouletteNeighbourCount(value) {
        this.update((draft) => {
            draft.rouletteNeighbourCount = value;
        });
    }

    public get rouletteNeighbourCount() {
        return this.value.rouletteNeighbourCount ?? DEFAULT_ROULETTE_NEIGHBOR_COUNT;
    }

    public set selectedVODByGame(value) {
        this.update((draft) => {
            draft.selectedVODByGame = value;
        });
    }

    public get selectedVODByGame() {
        return this.value.selectedVODByGame;
    }

    public setDealerLanguage(gameId: GameId, language: DealerLanguage) {
        this.update((draft) => {
            if (!draft.selectedVODByGame) {
                draft.selectedVODByGame = { [gameId]: { language, dealer: null } };
            } else {
                draft.selectedVODByGame[gameId].language = language;
            }
        });
    }

    public getDealerLanguage(gameId: GameId) {
        return this.value.selectedVODByGame?.[gameId]?.language ?? null;
    }

    public setDealer(gameId: GameId, dealer: string) {
        this.update((draft) => {
            if (!draft.selectedVODByGame) {
                draft.selectedVODByGame = { [gameId]: { dealer, language: null } };
            } else {
                draft.selectedVODByGame[gameId].dealer = dealer;
            }
        });
    }

    public getDealer(gameId: GameId) {
        return this.value.selectedVODByGame?.[gameId]?.dealer ?? null;
    }

    public set casinoFallbackTooltipSeen(value: boolean) {
        this.update((draft) => {
            draft.casino.fallbackTooltipSeen = value;
        });
    }

    public get casinoFallbackTooltipSeen() {
        return this.value.casino.fallbackTooltipSeen;
    }

    public set casinoWelcomeModalSeen(value: boolean) {
        this.update((draft) => {
            draft.casino.welcomeModalSeen = value;
        });
    }

    public get casinoWelcomeModalSeen() {
        return this.value.casino.welcomeModalSeen;
    }

    public set guiTipSeen(value: boolean) {
        this.update((draft) => {
            draft.gui.tipSeen = value;
        });
    }

    public get guiTipSeen() {
        return this.value.gui.tipSeen;
    }

    public set casinoMode(value: boolean) {
        this.update((draft) => {
            draft.casino.enabled = value;

            if (value) {
                draft.casino.welcomeModalSeen = true;
                draft.oddsFormat = BettingOptionsFormat.CASINO;
                // TODO: revert back to condition after release
                // we released settings before enabled casino mode on production for players
                // some player already populated settings in DB with oddsFormat decimal and won't be switched during launch day
                // if (!this.value.oddsFormat) {
                //     draft.oddsFormat = BettingOptionsFormat.CASINO;
                // }
            }
        });
    }

    public get casinoMode() {
        return this.value.casino.enabled;
    }

    public set oddsFormat(format: BettingOptionsFormat) {
        this.update((draft) => {
            draft.oddsFormat = format;
        });
    }

    public get oddsFormat() {
        return this.value.oddsFormat;
    }

    public set onlyRngAnimations(value: boolean) {
        this.update((draft) => {
            draft.onlyRngAnimations = value;
        });
    }

    public get onlyRngAnimations() {
        return this.value.onlyRngAnimations;
    }

    public set guiMode(value: boolean) {
        this.update((draft) => {
            if (value) {
                draft.gui.tipSeen = true;
            }

            draft.gui.enabled = value;
        });
    }

    public get guiMode() {
        return this.value.gui.enabled;
    }

    public set forcedGuiMode(value: boolean) {
        this.update((draft) => {
            draft.gui.forcedOnLowQuality = value;
        });
    }

    public get forcedGuiMode() {
        return this.value.gui.forcedOnLowQuality;
    }

    public resolveGuiMode(onlyGui: boolean, defaultGui: boolean): void {
        if (typeof onlyGui !== 'undefined') {
            this.guiMode = onlyGui;
        }

        if (typeof defaultGui !== 'undefined') {
            this.guiMode = defaultGui;
        }
    }

    public setAutoShowRouletteStatistics(value: boolean): void {
        this.update((draft) => {
            draft.autoShowRouletteStatistics = value;
        });
    }

    public get autoPlayTooltipSeen() {
        return this.value.autoPlayTooltipSeen;
    }

    public set autoPlayTooltipSeen(value: boolean) {
        this.update((draft) => {
            draft.autoPlayTooltipSeen = value;
        });
    }
}

export const playerSettingsStore = new PlayerSettingsStore();
